import Panzoom from '@panzoom/panzoom';
import { useEffect, useLayoutEffect, useRef } from 'react';

import type { Ref, RefObject } from 'react';
import type { PanzoomObject, PanzoomOptions } from '@panzoom/panzoom';

type RemoveIndexSignature<T> = {
  [P in keyof T as string extends P
    ? never
    : number extends P
    ? never
    : P]: T[P];
};

export type PanzoomRef = Pick<
  PanzoomObject,
  | 'getPan'
  | 'getScale'
  | 'pan'
  | 'reset'
  | 'zoom'
  | 'zoomIn'
  | 'zoomOut'
  | 'zoomToPoint'
  | 'zoomWithWheel'
>;

const __getDefaultCurrentValues = () => ({
  scale: 0,
  x: 0,
  y: 0,
});

const __getDefaultCurrentScale = () => __getDefaultCurrentValues().scale;

const DEFAULT_PANZOOM: PanzoomRef = {
  getPan: __getDefaultCurrentValues,
  getScale: __getDefaultCurrentScale,
  pan: __getDefaultCurrentValues,
  reset: __getDefaultCurrentValues,
  zoom: __getDefaultCurrentValues,
  zoomIn: __getDefaultCurrentValues,
  zoomOut: __getDefaultCurrentValues,
  zoomToPoint: __getDefaultCurrentValues,
  zoomWithWheel: __getDefaultCurrentValues,
};

export function usePanzoom<T extends HTMLElement | SVGElement>(
  options?: Omit<RemoveIndexSignature<PanzoomOptions>, 'force'>,
  deps?: unknown[],
): [Ref<T>, PanzoomRef];
export function usePanzoom<T extends HTMLElement | SVGElement>(
  ref: RefObject<T>,
  options?: Omit<RemoveIndexSignature<PanzoomOptions>, 'force'>,
  deps?: unknown[],
): PanzoomRef;

/** @internal */
export function usePanzoom<T extends HTMLElement | SVGElement>(
  providedRefOrOptions?:
    | RefObject<T>
    | Omit<RemoveIndexSignature<PanzoomOptions>, 'force'>,
  optionsOrDeps?:
    | unknown[]
    | Omit<RemoveIndexSignature<PanzoomOptions>, 'force'>,
  maybeDeps?: unknown[],
): unknown {
  let deps: unknown[];
  let options: Omit<RemoveIndexSignature<PanzoomOptions>, 'force'> | undefined;
  let providedRef: RefObject<T> | null;

  // overload 2
  if (
    typeof providedRefOrOptions === 'object' &&
    'current' in providedRefOrOptions
  ) {
    if (Array.isArray(optionsOrDeps)) {
      throw new TypeError('invalid arguments');
    }

    deps = maybeDeps ?? [];
    options = optionsOrDeps;
    providedRef = providedRefOrOptions;
  } else {
    if (optionsOrDeps !== undefined && !Array.isArray(optionsOrDeps)) {
      throw new TypeError('invalid arguments');
    }

    deps = optionsOrDeps ?? [];
    options = providedRefOrOptions;
    providedRef = null;
  }

  const defaultRef = useRef<T>(null);
  const ref = providedRef ?? defaultRef;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const panzoomRef = useRef<PanzoomObject>(DEFAULT_PANZOOM as PanzoomObject);

  useEffect(() => {
    panzoomRef.current.setOptions?.(options);
  }, [options]);

  useLayoutEffect(() => {
    if (!ref.current) return;

    panzoomRef.current = Object.assign(
      panzoomRef.current,
      Panzoom(ref.current, optionsRef.current),
    );

    return () => {
      panzoomRef.current.destroy();
      panzoomRef.current = Object.assign(panzoomRef.current, DEFAULT_PANZOOM);
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  if (providedRef) return panzoomRef.current;
  else return [ref, panzoomRef.current];
}
